import { useState, useCallback, useEffect, useMemo } from 'react';
import { trpc, trpcClient } from '@/api/trpc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/Layout/Toast';

export type ParameterStatus = 'pinned' | 'normal' | 'archived';

export interface ParameterPreferenceIdentity {
    preferenceKey: string;
    featureId?: string | null;
    featureKey?: string | null;
    contextFeatureRef?: string | null;
}

interface UseParameterPreferencesOptions {
    enabled?: boolean;
}

interface ParameterPreferenceIdentityPayload {
    featureId?: string;
    featureKey?: string;
    contextFeatureRef: string;
}

interface ParameterPreference {
    featureId: string;
    featureKey: string;
    contextFeatureRef: string;
    status: ParameterStatus;
}

export type ParameterPreferenceStatusUpdate = ParameterPreferenceIdentity & {
    status: ParameterStatus;
};

type ParameterPreferenceStatusPayload = ParameterPreferenceIdentityPayload & {
    status: ParameterStatus;
};

type ResetParameterPreferencesPayload = {
    parameterIdentities?: ParameterPreferenceIdentityPayload[];
};

const hasText = (value: string | null | undefined): value is string => typeof value === 'string' && value.length > 0;

const getPreferenceKey = (preference: ParameterPreference): string => {
    return preference.featureId;
};

const buildIdentityPayload = (identity: ParameterPreferenceIdentity): ParameterPreferenceIdentityPayload => {
    if (!hasText(identity.contextFeatureRef)) {
        throw new Error('Canonical context feature reference is required for parameter preference updates');
    }

    return {
        ...(hasText(identity.featureId) ? { featureId: identity.featureId } : {}),
        ...(hasText(identity.featureKey) ? { featureKey: identity.featureKey } : {}),
        contextFeatureRef: identity.contextFeatureRef
    };
};

const buildStatusPayload = (identity: ParameterPreferenceIdentity, status: ParameterStatus): ParameterPreferenceStatusPayload => {
    return {
        ...buildIdentityPayload(identity),
        status
    };
};

export function useParameterPreferences({ enabled = true }: UseParameterPreferencesOptions) {
    const queryClient = useQueryClient();
    const [preferences, setPreferences] = useState<Map<string, ParameterStatus>>(new Map());
    const [showArchived, setShowArchived] = useState(false);
    const [viewMode, setViewMode] = useState<'all' | 'pinned'>('all');
    const [isUpdating, setIsUpdating] = useState(false);

    // Fetch preferences from backend using queryOptions pattern (global across all experiments)
    const preferencesQuery = useQuery({
        ...trpc.experiment.getParameterPreferences.queryOptions({}),
        enabled,
        staleTime: 300000 // Cache for 5 minutes (global data changes less frequently)
    });

    // Update local state when query data changes (in useEffect to follow React rules)
    useEffect(() => {
        if (preferencesQuery.data?.preferences && !preferencesQuery.isLoading) {
            const newPrefs = new Map<string, ParameterStatus>();

            preferencesQuery.data.preferences.forEach((pref) => {
                newPrefs.set(getPreferenceKey(pref), pref.status);
            });

            const timeoutId = window.setTimeout(() => {
                // Only update if actually different
                setPreferences((current) => {
                    const hasChanged =
                        newPrefs.size !== current.size || Array.from(newPrefs.entries()).some(([k, v]) => current.get(k) !== v);

                    return hasChanged ? newPrefs : current;
                });
            }, 0);

            return () => window.clearTimeout(timeoutId);
        }

        return undefined;
    }, [preferencesQuery.data, preferencesQuery.isLoading]);

    // Helper functions
    const getStatus = useCallback(
        (preferenceKey: string): ParameterStatus => {
            return preferences.get(preferenceKey) || 'normal';
        },
        [preferences]
    );

    const setStatus = useCallback(
        async (identity: ParameterPreferenceIdentity, status: ParameterStatus) => {
            try {
                const payload = buildStatusPayload(identity, status);
                setIsUpdating(true);

                // Optimistic update
                setPreferences((prev) => {
                    const next = new Map(prev);

                    if (status === 'normal') {
                        next.delete(identity.preferenceKey);
                    } else {
                        next.set(identity.preferenceKey, status);
                    }

                    return next;
                });

                // Send to backend (global update across all experiments)
                const result = await trpcClient.experiment.setParameterStatus.mutate(payload);

                if (result.success) {
                    // Invalidate the query to ensure consistency
                    void queryClient.invalidateQueries({
                        queryKey: trpc.experiment.getParameterPreferences.queryOptions({}).queryKey
                    });
                }
            } catch (error) {
                // Revert optimistic update on error
                await preferencesQuery.refetch();
                showToast(`Failed to update parameter: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            } finally {
                setIsUpdating(false);
            }
        },
        [queryClient, preferencesQuery]
    );

    const togglePin = useCallback(
        (identity: ParameterPreferenceIdentity) => {
            const currentStatus = getStatus(identity.preferenceKey);
            const newStatus = currentStatus === 'pinned' ? 'normal' : 'pinned';
            void setStatus(identity, newStatus);
        },
        [getStatus, setStatus]
    );

    const toggleArchive = useCallback(
        (identity: ParameterPreferenceIdentity) => {
            const currentStatus = getStatus(identity.preferenceKey);
            const newStatus = currentStatus === 'archived' ? 'normal' : 'archived';
            void setStatus(identity, newStatus);
        },
        [getStatus, setStatus]
    );

    const bulkSetStatus = useCallback(
        async (updates: ParameterPreferenceStatusUpdate[]) => {
            try {
                setIsUpdating(true);
                const updatePayloads = updates.map((update) => buildStatusPayload(update, update.status));

                const result = await trpcClient.experiment.setParameterStatusBulk.mutate({
                    updates: updatePayloads
                });

                if (result.success) {
                    showToast(`Updated ${result.updated} parameters`, 'success');
                    // Refetch to get latest state
                    await preferencesQuery.refetch();
                } else if (result.failed > 0) {
                    showToast(`Updated ${result.updated} parameters, ${result.failed} failed`, 'warning');
                    await preferencesQuery.refetch();
                }
            } catch (error) {
                showToast(`Failed to bulk update: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            } finally {
                setIsUpdating(false);
            }
        },
        [preferencesQuery]
    );

    const resetAll = useCallback(
        async (identities?: ParameterPreferenceIdentity[]) => {
            try {
                setIsUpdating(true);
                const canonicalIdentities = identities?.filter((identity) => hasText(identity.contextFeatureRef));

                const payload: ResetParameterPreferencesPayload =
                    canonicalIdentities && canonicalIdentities.length > 0
                        ? {
                              parameterIdentities: canonicalIdentities.map(buildIdentityPayload)
                          }
                        : {};

                if (identities && identities.length > 0 && (payload.parameterIdentities?.length ?? 0) === 0) {
                    showToast('No canonical parameter preferences to reset', 'warning');
                    return;
                }

                const result = await trpcClient.experiment.resetPreferences.mutate(payload);

                if (result.success) {
                    // If specific paths provided, only clear those from local state
                    if (identities && identities.length > 0) {
                        setPreferences((prev) => {
                            const next = new Map(prev);
                            canonicalIdentities?.forEach((identity) => next.delete(identity.preferenceKey));
                            return next;
                        });
                    } else {
                        // Clear all if no specific paths
                        setPreferences(new Map());
                    }

                    showToast(result.message, 'success');

                    // Invalidate the query
                    void queryClient.invalidateQueries({
                        queryKey: trpc.experiment.getParameterPreferences.queryOptions({}).queryKey
                    });
                }
            } catch (error) {
                showToast(`Failed to reset preferences: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            } finally {
                setIsUpdating(false);
            }
        },
        [queryClient]
    );

    // Filter function for parameters based on preferences
    const shouldShowParameter = useCallback(
        (preferenceKey: string): boolean => {
            const status = getStatus(preferenceKey);

            // In pinned only mode, only show pinned
            if (viewMode === 'pinned') {
                return status === 'pinned';
            }

            // Otherwise show based on archive setting
            if (status === 'archived' && !showArchived) {
                return false;
            }

            return true;
        },
        [getStatus, viewMode, showArchived]
    );

    // Sort function to put pinned at top
    const sortByPreference = useCallback(
        (a: { preferenceKey: string }, b: { preferenceKey: string }): number => {
            const aStatus = getStatus(a.preferenceKey);
            const bStatus = getStatus(b.preferenceKey);

            // Pinned items always come first
            if (aStatus === 'pinned' && bStatus !== 'pinned') return -1;
            if (aStatus !== 'pinned' && bStatus === 'pinned') return 1;

            // Then normal, then archived
            if (aStatus === 'normal' && bStatus === 'archived') return -1;
            if (aStatus === 'archived' && bStatus === 'normal') return 1;

            return 0;
        },
        [getStatus]
    );

    // Memoize count calculations for better performance
    const counts = useMemo(() => {
        let pinned = 0;
        let archived = 0;

        for (const status of preferences.values()) {
            if (status === 'pinned') pinned++;
            if (status === 'archived') archived++;
        }

        return { pinned, archived };
    }, [preferences]);

    return {
        // State
        preferences,
        showArchived,
        setShowArchived,
        viewMode,
        setViewMode,

        // Loading states
        isLoading: preferencesQuery.isLoading,
        isUpdating,

        // Functions
        getStatus,
        setStatus,
        togglePin,
        toggleArchive,
        bulkSetStatus,
        resetAll,
        shouldShowParameter,
        sortByPreference,

        // Counts (memoized for performance)
        pinnedCount: counts.pinned,
        archivedCount: counts.archived
    };
}
